import { useState, useEffect } from "react";
import { Bell, Heart, MessageSquare, Check, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";

interface Notification {
  id: string;
  type: "like" | "comment";
  actor_id: string;
  post_id: string;
  comment_id: string | null;
  is_read: boolean;
  created_at: string;
  actor?: {
    name: string;
    avatar: string;
    username?: string;
  };
  post?: {
    title: string;
  };
}

export const NotificationBell = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user]);

  // Real-time subscription for new notifications
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("notifications-realtime")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        async (payload) => {
          console.log("New notification:", payload);
          const newNotif = payload.new as any;

          // Fetch actor profile
          const { data: actorProfile } = await supabase
            .from("profiles")
            .select("full_name, avatar_url, username")
            .eq("user_id", newNotif.actor_id)
            .single();

          // Fetch post title
          const { data: post } = await supabase
            .from("community_posts")
            .select("title")
            .eq("id", newNotif.post_id)
            .single();

          const enrichedNotification: Notification = {
            ...newNotif,
            actor: {
              name: actorProfile?.full_name || "Someone",
              avatar:
                actorProfile?.avatar_url ||
                `https://api.dicebear.com/7.x/initials/svg?seed=${actorProfile?.full_name || "U"}`,
              username: actorProfile?.username || undefined,
            },
            post: {
              title: post?.title || "a post",
            },
          };

          setNotifications((prev) => [enrichedNotification, ...prev]);
          setUnreadCount((prev) => prev + 1);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const deletedId = (payload.old as any)?.id;
          if (deletedId) {
            setNotifications((prev) => prev.filter((n) => n.id !== deletedId));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const fetchNotifications = async () => {
    if (!user) return;

    setIsLoading(true);

    const { data, error } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(20);

    if (error) {
      console.error("Error fetching notifications:", error);
      setIsLoading(false);
      return;
    }

    if (!data || data.length === 0) {
      setNotifications([]);
      setUnreadCount(0);
      setIsLoading(false);
      return;
    }

    // Fetch actor profiles
    const actorIds = [...new Set(data.map((n) => n.actor_id))];
    const { data: profiles } = await supabase
      .from("profiles")
      .select("user_id, full_name, avatar_url, username")
      .in("user_id", actorIds);

    const profileMap = new Map(profiles?.map((p) => [p.user_id, p]) || []);

    // Fetch post titles
    const postIds = [...new Set(data.map((n) => n.post_id))];
    const { data: posts } = await supabase
      .from("community_posts")
      .select("id, title")
      .in("id", postIds);

    const postMap = new Map(posts?.map((p) => [p.id, p]) || []);

    const enrichedNotifications: Notification[] = data.map((notif) => {
      const actor = profileMap.get(notif.actor_id);
      const post = postMap.get(notif.post_id);
      return {
        ...notif,
        type: notif.type as "like" | "comment",
        actor: {
          name: actor?.full_name || "Someone",
          avatar:
            actor?.avatar_url ||
            `https://api.dicebear.com/7.x/initials/svg?seed=${actor?.full_name || "U"}`,
          username: actor?.username || undefined,
        },
        post: {
          title: post?.title || "a post",
        },
      };
    });

    setNotifications(enrichedNotifications);
    setUnreadCount(enrichedNotifications.filter((n) => !n.is_read).length);
    setIsLoading(false);
  };

  const markAsRead = async (notificationId: string) => {
    await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("id", notificationId);

    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  };

  const markAllAsRead = async () => {
    if (!user) return;

    await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("user_id", user.id)
      .eq("is_read", false);

    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const deleteNotification = async (notificationId: string) => {
    const notif = notifications.find((n) => n.id === notificationId);

    await supabase.from("notifications").delete().eq("id", notificationId);

    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    if (notif && !notif.is_read) {
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
  };

  const clearAll = async () => {
    if (!user) return;

    await supabase.from("notifications").delete().eq("user_id", user.id);

    setNotifications([]);
    setUnreadCount(0);
  };

  if (!user) return null;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-lg hover:bg-secondary transition-colors">
          <Bell className="w-5 h-5 text-muted-foreground" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-primary text-primary-foreground text-xs font-bold rounded-full flex items-center justify-center animate-in zoom-in">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        align="end"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-border">
          <h3 className="font-semibold text-foreground">Notifications</h3>
          {notifications.length > 0 && (
            <div className="flex gap-1">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={markAllAsRead}
                >
                  <Check className="w-3 h-3 mr-1" />
                  Mark all read
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Notifications List */}
        <ScrollArea className="max-h-80">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
            </div>
          ) : notifications.length > 0 ? (
            <div className="divide-y divide-border">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    "flex gap-3 p-3 hover:bg-secondary/50 transition-colors group",
                    !notification.is_read && "bg-primary/5"
                  )}
                >
                  {/* Icon */}
                  <div
                    className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                      notification.type === "like"
                        ? "bg-red-500/10 text-red-500"
                        : "bg-blue-500/10 text-blue-500"
                    )}
                  >
                    {notification.type === "like" ? (
                      <Heart className="w-4 h-4" />
                    ) : (
                      <MessageSquare className="w-4 h-4" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground">
                      <span className="font-medium">
                        {notification.actor?.name}
                      </span>{" "}
                      {notification.type === "like"
                        ? "liked"
                        : "commented on"}{" "}
                      your post{" "}
                      <Link
                        to="/community"
                        className="font-medium text-primary hover:underline"
                        onClick={() => {
                          setIsOpen(false);
                          if (!notification.is_read) {
                            markAsRead(notification.id);
                          }
                        }}
                      >
                        "{notification.post?.title?.slice(0, 30)}
                        {(notification.post?.title?.length || 0) > 30
                          ? "..."
                          : ""}"
                      </Link>
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {formatDistanceToNow(new Date(notification.created_at), {
                        addSuffix: true,
                      })}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {!notification.is_read && (
                      <button
                        onClick={() => markAsRead(notification.id)}
                        className="p-1 text-muted-foreground hover:text-foreground"
                        title="Mark as read"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => deleteNotification(notification.id)}
                      className="p-1 text-muted-foreground hover:text-destructive"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center">
              <Bell className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                No notifications yet
              </p>
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-2 border-t border-border">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs text-muted-foreground"
              onClick={clearAll}
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Clear all
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
